/**
 * Zod validation schemas for all forms and inputs.
 * Centralizes validation logic for consistency and reusability.
 */

import { z } from "zod";
import { BOUNTY_CONFIG, AUTH_CONFIG } from "@/config/constants";
import { validateProse, type BountyProseField } from "@/lib/prose";

// Auth form schema
export const authSchema = z.object({
  email: z.string().trim().email("Invalid email address").max(AUTH_CONFIG.MAX_EMAIL_LENGTH, "Email too long"),
  password: z
    .string()
    .min(AUTH_CONFIG.MIN_PASSWORD_LENGTH, `Password must be at least ${AUTH_CONFIG.MIN_PASSWORD_LENGTH} characters`)
    .max(AUTH_CONFIG.MAX_PASSWORD_LENGTH, "Password too long"),
});

export type AuthFormData = z.infer<typeof authSchema>;

export const bountyProseSchema = (field: BountyProseField) => z.string().transform((input, context) => {
  const result = validateProse(input, field);
  if (result.allowed === false) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: result.reason });
    return z.NEVER;
  }
  return result.sanitizedInput;
});

// Create bounty form schema
export const createBountySchema = z.object({
  title: bountyProseSchema("title"),
  description: bountyProseSchema("description"),
  location: bountyProseSchema("location").nullish(),
  instructions: bountyProseSchema("instructions").nullish(),
  proof_type: z.enum(["photo", "screenshot", "link", "text", "video", "document", "audio"]),
});

// Shared bounty policy; clients cannot derive eligibility from draft flags.
export const BOUNTY_TERMS_LIMITS = {
  freeMinRewardCents: BOUNTY_CONFIG.FREE_MIN_REWARD_CENTS,
  proMinRewardCents: BOUNTY_CONFIG.PRO_MIN_REWARD_CENTS,
  maxRewardCents: BOUNTY_CONFIG.PRO_MAX_REWARD_CENTS,
  maxClaimers: BOUNTY_CONFIG.MAX_HUNTERS,
  freeMaxDurationHours: BOUNTY_CONFIG.FREE_MAX_DURATION_HOURS,
  proMaxDurationHours: BOUNTY_CONFIG.PRO_MAX_DURATION_HOURS,
} as const;

/**
 * Supply a server-verified tier, never a draft's isPro/promo/free-credit flag.
 * These checks are client feedback, not authorization; the RPC must enforce them.
 * Money is integer cents. Durations are integer hours. No coercion or rounding.
 */
export function createBountyTermsSchema(isPro = false) {
  const pro = isPro === true;
  return z.object({
    rewardCents: z.number().finite().int()
      .min(pro ? BOUNTY_TERMS_LIMITS.proMinRewardCents : BOUNTY_TERMS_LIMITS.freeMinRewardCents)
      .max(pro ? BOUNTY_CONFIG.PRO_MAX_REWARD_CENTS : BOUNTY_CONFIG.FREE_MAX_REWARD_CENTS),
    maxClaimers: z.number().finite().int().min(BOUNTY_CONFIG.MIN_HUNTERS).max(BOUNTY_TERMS_LIMITS.maxClaimers).default(BOUNTY_CONFIG.MIN_HUNTERS),
    durationHours: z.number().finite().int().min(1)
      .max(pro ? BOUNTY_TERMS_LIMITS.proMaxDurationHours : BOUNTY_TERMS_LIMITS.freeMaxDurationHours).default(BOUNTY_CONFIG.DEFAULT_DURATION_HOURS),
  }).strict();
}

export type BountyTerms = z.infer<ReturnType<typeof createBountyTermsSchema>>;

export type CreateBountyFormData = z.infer<typeof createBountySchema>;

// Helper function for safe form validation
export function validateForm<T extends z.ZodTypeAny>(schema: T, data: unknown): { success: true; data: z.infer<T> } | { success: false; error: string } {
  const result = schema.safeParse(data);
  if (!result.success) {
    return {
      success: false,
      error: result.error.issues[0]?.message || "Validation failed",
    };
  }
  return { success: true, data: result.data };
}
